import React, { useState } from "react";
import NewPageHeader from "../utils/NewPageHeader";
import { Container, Typography } from "@mui/material";

const articles = [
  {
    id: 1,
    category: "Research",
    date: "February 2024",
    title: "U.S. Prescription Drug Prices Are 278% Higher Than in Other Nations",
    excerpt:
      "A landmark RAND Corporation study using 2022 data found that Americans pay nearly three times more for prescription drugs than people in 33 other high-income countries. For brand-name drugs, U.S. prices averaged 422% of those abroad. Even after accounting for rebates, U.S. net prices for brand-name drugs remain more than three times higher. Unbranded generics are the one exception — they cost about 67% of the international average.",
    readTime: "6 min read",
    tag: "Global Pricing",
    color: "#43C5B9",
    source: "RAND Corporation",
    sourceUrl: "https://www.rand.org/news/press/2024/02/01.html",
    fullContent: {
      intro:
        "A comprehensive study by the RAND Corporation, released in February 2024, analyzed prescription drug pricing using 2022 data across 34 countries. The findings reveal a stark pricing disparity that continues to widen — particularly for brand-name drugs.",
      sections: [
        {
          heading: "Key Findings at a Glance",
          bullets: [
            "U.S. drug prices averaged 278% of those in 33 comparison countries across all drug types.",
            "For brand-name originator drugs, U.S. gross prices were 422% of prices in comparison countries.",
            "Even after rebates and discounts, U.S. net prices for brand-name drugs were more than 3× higher than abroad.",
            "Unbranded generics were cheaper in the U.S. — about 67% of the international average — but they represent 90% of U.S. volume vs. 41% internationally.",
            "The United States accounted for 62% of total drug sales among OECD nations in 2022, despite representing only 24% of the prescription volume.",
          ],
        },
        {
          heading: "Widening Gap for Brand-Name Drugs",
          body:
            "Lead author Andrew Mulcahy, a senior health economist at RAND, noted that the price gap for brand-name drugs is widening compared to earlier analyses, while the advantage for generic drugs has grown. Specialty drugs like Humira, Harvoni, and Enbrel are priced 265%–423% higher in the U.S. than in the UK and Switzerland. Weight-loss drugs such as Ozempic and Wegovy are listed at 2 to 10 times (200%–1,000%) the prices charged in peer nations.",
        },
        {
          heading: "Country-by-Country Variation",
          body:
            "Among the 33 OECD comparison countries, U.S. prices ranged from 172% of prices in Mexico to 1,028% of prices in Turkey. The variation underscores that there is no single 'international price' — but the U.S. consistently sits at the top of the range for branded medications.",
        },
        {
          heading: "New Biosimilar Analysis",
          body:
            "The 2024 report introduced new analyses on biosimilars — biological drugs that are near-copies of existing biologics. Despite the introduction of biosimilars in recent years (e.g., Humira biosimilars), the U.S. net prices for the originator brand-name versions remain dramatically higher than in comparison nations, slowing the potential savings biosimilars could bring to U.S. patients.",
        },
        {
          heading: "Why the Gap Exists",
          body:
            "Unlike most developed countries, the U.S. has no national drug price negotiation system for most payers. Pharmaceutical companies can set launch prices freely. Pharmacy Benefit Managers (PBMs) negotiate confidential rebates — meaning list prices and actual costs diverge significantly, making transparency difficult. The 2022 Inflation Reduction Act introduced limited Medicare negotiating power, but for the broader market, manufacturer pricing power remains largely unchecked.",
        },
        {
          heading: "What This Means for Patients",
          body:
            "The price disparity directly translates into higher out-of-pocket costs, higher premiums, and more Americans rationing or skipping medications. According to KFF polling in 2023, more than 1 in 4 adults taking prescription drugs report difficulty affording their medication. Among those earning under $40,000/year, that rate climbs to 40%. The RAND data provides concrete, internationally validated evidence for why U.S. drug prices need systematic reform.",
        },
      ],
    },
  },
  {
    id: 2,
    category: "Analysis",
    date: "May 2024",
    title: "$722 Billion: America's Record-Breaking Prescription Drug Tab in 2023",
    excerpt:
      "U.S. spending on prescription drugs hit $722 billion in 2023 — a 13.6% surge from 2022 and the largest single-year increase in 20 years. Semaglutide drugs (Ozempic, Wegovy, Rybelsus) topped all spending at $38.58 billion alone. Humira and Eliquis rounded out the top three. Three factors are driving the spike: rising utilization, new drug approvals, and price increases — though experts say price hikes are the least significant of the three.",
    readTime: "7 min read",
    tag: "Drug Spending",
    color: "#2db5a8",
    source: "Healthline / AJHP Study",
    sourceUrl: "https://www.healthline.com/health-news/top-25-prescription-drugs-spending",
    fullContent: {
      intro:
        "According to a study published in the American Journal of Health-System Pharmacy in April 2024, the U.S. spent more than $722 billion on prescription drugs in 2023 — a 13.6% increase from 2022. This is the largest single-year uptick in national drug spending in two decades.",
      sections: [
        {
          heading: "Top 10 Drugs by U.S. Spending in 2023",
          bullets: [
            "1. Semaglutide (Ozempic, Rybelsus, Wegovy) — $38.58 billion",
            "2. Adalimumab (Humira) — $35.33 billion",
            "3. Apixaban (Eliquis) — $22.11 billion",
            "4. Dulaglutide (Trulicity) — $16.28 billion",
            "5. Empagliflozin (Jardiance) — $15.89 billion",
            "6. Ustekinumab (Stelara) — $15.87 billion",
            "7. Pembrolizumab (Keytruda) — $15.40 billion",
            "8. Tirzepatide (Mounjaro, Zepbound) — $13.16 billion",
            "9. Biktarvy — $13.15 billion",
            "10. Dupilumab (Dupixent) — $11.49 billion",
          ],
        },
        {
          heading: "Full Top 25 (Ranks 11–25)",
          bullets: [
            "11. Risankizumab (Skyrizi) — $10.10 billion",
            "12. Etanercept (Enbrel) — $9.55 billion",
            "13. Insulin glargine (Lantus, Toujeo, Basaglar) — $8.89 billion",
            "14. Rivaroxaban (Xarelto) — $8.26 billion",
            "15. Dapagliflozin (Farxiga) — $7.90 billion",
            "16. Immune globulin — $6.27 billion",
            "17. Insulin aspart (NovoLog) — $5.79 billion",
            "18. Secukinumab (Cosentyx) — $5.72 billion",
            "19. Sitagliptin (Januvia) — $5.72 billion",
            "20. Fluticasone/umeclidinium/vilanterol (Trelegy Ellipta) — $5.71 billion",
            "21. Nivolumab (Opdivo) — $5.39 billion",
            "22. Lisdexamfetamine (Vyvanse) — $5.36 billion",
            "23. Insulin lispro (Humalog) — $5.32 billion",
            "24. Ixekizumab (Taltz) — $5.31 billion",
            "25. Ocrelizumab (Ocrevus) — $5.30 billion",
          ],
        },
        {
          heading: "Why Spending Surged: Three Primary Drivers",
          bullets: [
            "Utilization (+6.5%): When drugs gain new FDA-approved uses, more patients qualify. Semaglutide (Wegovy) received approval for heart attack risk reduction in March 2024 — expected to push utilization even higher.",
            "New Drugs (+4.2%): The FDA approved 55 new drugs in 2023, nearly half classified as 'first-in-class.' Biologics and gene therapies are costly to develop and command premium prices.",
            "Price Increases (+2.9%): Drug prices ticked up ~3% in 2023, the smallest contributor — most insured patients are partially shielded from these increases.",
          ],
        },
        {
          heading: "An Aging Population Is Driving Demand",
          body:
            "62 million Americans are now 65 or older. About 80% of older adults take at least two prescription drugs regularly, and 36% take five or more. As chronic conditions like diabetes, cardiovascular disease, and arthritis grow more prevalent, demand for ongoing medication management rises with them. Experts expect this demographic trend to continue accelerating drug spending for the next decade.",
        },
        {
          heading: "Can Spending Be Reduced?",
          body:
            "Policy experts point to government price negotiation as the most powerful lever. The 2022 Inflation Reduction Act allows Medicare to negotiate prices for selected drugs — starting with 10 drugs taking effect in 2026 — and caps out-of-pocket insulin costs at $35/month for Medicare patients. Competition among GLP-1 anti-obesity drugs (like Ozempic and Wegovy) already drove Wegovy prices down in Q1 2024. Shifting from treatment to prevention is another long-term strategy, though experts caution that legislative progress remains slow due to pharmaceutical industry resistance.",
        },
      ],
    },
  },
  {
    id: 3,
    category: "Research",
    date: "2024",
    title: "Forecasted Trends in U.S. Prescription Drug Spending: What's Coming Next",
    excerpt:
      "Drug spending per capita accelerated in 2021 and adjusted for inflation now stands at $1,147 — up from just $101 in 1960. While NHEA projections expect moderate growth through 2031, out-of-pocket prescription costs are forecast to decline by 8% by then, even as hospital and physician out-of-pocket costs rise. 82% of U.S. adults say drug prices are unreasonable, and 88% favor government limits on annual price increases.",
    readTime: "8 min read",
    tag: "Policy & Trends",
    color: "#1e9e93",
    source: "Peterson-KFF Health System Tracker",
    sourceUrl: "https://www.healthsystemtracker.org/chart-collection/recent-forecasted-trends-prescription-drug-spending/",
    fullContent: {
      intro:
        "The Peterson-KFF Health System Tracker presents a comprehensive collection of charts and data on prescription drug spending trends — both historical and projected — revealing how decades of policy choices and market dynamics have shaped today's drug pricing crisis.",
      sections: [
        {
          heading: "Decades of Rising Spending",
          body:
            "Adjusted for inflation, U.S. retail prescription drug spending per capita rose from $101 in 1960 to $1,147 in 2021 — more than an 11-fold increase. Spending accelerated sharply in the 1990s with the introduction of blockbuster drugs and again between 2013–2015 when two high-cost specialty hepatitis C drugs (Harvoni and Sovaldi) entered the market. After a slight dip in 2020 during the COVID-19 pandemic, per capita spending bounced back in 2021.",
        },
        {
          heading: "Forecasts Through 2031",
          body:
            "Government projections (NHEA) suggest that growth in per capita drug spending will moderate over the next decade. However, 'moderate' still means continued rises in absolute spending. Importantly, out-of-pocket prescription drug spending is projected to decline by approximately 8% by 2031 — driven largely by the Inflation Reduction Act's Medicare provisions — while out-of-pocket hospital and physician spending are expected to increase by 44% and 43% respectively.",
        },
        {
          heading: "Out-of-Pocket Spending Trends",
          bullets: [
            "Retail prescription drug out-of-pocket spending per capita: $147 (2019) → $147 (2020) → $151 (2021).",
            "Out-of-pocket hospital services: $98/capita (2020) → $103/capita (2021).",
            "Physician services saw the sharpest recent increase: $183/capita (2020) → $199/capita (2021), a 9% rise.",
            "Out-of-pocket prescription drug spending has declined since the mid-2000s thanks to expanded insurance coverage.",
            "By 2031, out-of-pocket drug costs are forecast to fall 8%, while physician (+43%) and hospital (+44%) costs will climb.",
          ],
        },
        {
          heading: "U.S. Prices vs. International Peers",
          body:
            "U.S. list prices for specialty drugs dramatically outpace peer nations. Harvoni (hepatitis C) is listed 132% higher than in Switzerland and 89% more than the UK. Humira (autoimmune) carries a 265% premium over Switzerland and 423% over the UK. Enbrel (rheumatoid arthritis) is 196% higher than Switzerland and 403% higher than the UK. Weight-loss drugs like Ozempic are listed at 2 to 10 times peer-nation prices in the U.S.",
        },
        {
          heading: "Medicare's Growing Role",
          body:
            "Medicare Part D, launched in 2006, dramatically shifted the payer landscape. Between 2005 and 2021, Medicare's share of total national prescription drug spending rose from 2% to 32%, making it the second largest payer after private insurance. In 2021, retail prescription drugs accounted for 8.9% of all U.S. health spending, but a disproportionate 16.1% of fully-insured private health plan premiums after rebates.",
        },
        {
          heading: "What Americans Think",
          bullets: [
            "82% of U.S. adults say the cost of prescription drugs is unreasonable (KFF 2023).",
            "65% of adults say affording their medication is 'very' or 'somewhat' easy — but this masks sharp disparities.",
            "Adults who are younger, lower-income, or take 4+ medications are significantly more likely to report difficulty affording drugs.",
            "88% of Americans favor limiting annual drug price increases to no more than the rate of inflation.",
            "Large majorities support allowing Medicare to negotiate prices and taxing companies that refuse to negotiate.",
            "25% oppose allowing Medicare drug plans to exclude more drugs from formularies.",
          ],
        },
        {
          heading: "The Inflation Reduction Act's Impact",
          body:
            "The 2022 Inflation Reduction Act represents the most significant U.S. drug pricing legislation in decades. It removes cost-sharing for vaccines under Medicare Part D, caps monthly insulin costs at $35 for Medicare enrollees, limits total out-of-pocket spending under Medicare Part D, and gives Medicare the power to negotiate prices for select high-cost drugs (10 drugs initially, with more added annually from 2026 onward). Projections show this will meaningfully shift out-of-pocket prescription spending downward over the coming decade.",
        },
      ],
    },
  },
  {
    id: 4,
    category: "Research",
    date: "March 2020",
    title: "Drug Pricing and Financial Toxicity in Multiple Myeloma",
    excerpt:
      "This study investigates how high drug prices create financial toxicity for multiple myeloma patients, examining the downstream impact on treatment adherence and patient outcomes. The findings highlight a systemic burden placed on patients by unchecked specialty drug pricing.",
    readTime: "5 min read",
    tag: "Financial Toxicity",
    color: "#43C5B9",
    source: "Nature — Blood Cancer Journal",
    sourceUrl: "https://www.nature.com/articles/s41408-020-0338-x",
    fullContent: {
      intro:
        "Published in Blood Cancer Journal (Nature), this study examines the intersection of high drug costs and patient financial strain in the context of multiple myeloma — a blood cancer requiring long-term, expensive therapy.",
      sections: [
        {
          heading: "Key Findings",
          bullets: [
            "Financial toxicity is a measurable and significant side effect of cancer drug pricing.",
            "Patients report skipping doses, splitting pills, or abandoning treatment due to cost.",
            "High out-of-pocket costs for myeloma drugs correlate with worse adherence and outcomes.",
            "The study calls for structural policy reforms to protect patients from drug-related financial ruin.",
          ],
        },
      ],
    },
  },
  {
    id: 5,
    category: "Research",
    date: "April 2021",
    title: "Factors Associated with High U.S. Drug Prices and the Global Disparity",
    excerpt:
      "A comprehensive analysis of structural, regulatory, and market-based factors that explain why U.S. drug prices consistently outpace those in other high-income countries. The study points to patent protections, PBM opacity, and the absence of national price negotiation as primary drivers.",
    readTime: "6 min read",
    tag: "Global Pricing",
    color: "#2db5a8",
    source: "PMC / NCBI",
    sourceUrl: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8079839/",
    fullContent: {
      intro:
        "This peer-reviewed article from PMC/NCBI systematically examines the drivers behind the well-documented gap between U.S. drug prices and those in peer nations, analyzing both supply-side and demand-side factors.",
      sections: [
        {
          heading: "Key Factors Identified",
          bullets: [
            "Lack of centralized price negotiation unlike the UK's NICE or Germany's AMNOG systems.",
            "Extensive patent protections and exclusivity periods delay generic competition.",
            "Pharmacy Benefit Manager (PBM) rebate opacity obscures true net pricing.",
            "U.S. insurers bear higher administrative costs, inflating drug distribution costs.",
            "Direct-to-consumer advertising inflates perceived brand value and demand.",
          ],
        },
      ],
    },
  },
  {
    id: 6,
    category: "Research",
    date: "2024",
    title: "Prescription Drug Pricing Policies Across High-Income Countries",
    excerpt:
      "A cross-national comparison of prescription drug pricing policies, examining how health technology assessment and price negotiation shape affordability across OECD nations — and what the U.S. can learn from these models.",
    readTime: "7 min read",
    tag: "Policy & Reform",
    color: "#1e9e93",
    source: "JAMA Health Forum",
    sourceUrl: "https://jamanetwork.com/journals/jama-health-forum/fullarticle/2841230",
    fullContent: {
      intro:
        "Published in JAMA Health Forum, this study provides a rigorous cross-national comparison of how high-income countries structure their drug pricing policies — and the measurable impact on patient affordability and access.",
      sections: [
        {
          heading: "Policy Approaches Compared",
          bullets: [
            "Countries like Germany, France, and Canada use health technology assessment (HTA) to set reference prices.",
            "The UK's NICE evaluates cost-effectiveness before approving reimbursement.",
            "Australia's Pharmaceutical Benefits Scheme (PBS) leverages national purchasing power.",
            "The U.S. lacks a unified HTA body and allows manufacturers to set launch prices freely.",
            "External reference pricing (ERP) — used by 25+ countries — anchors prices to international averages.",
          ],
        },
      ],
    },
  },
];



export default function HealthcareInAmerica() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const categories = ["All", "Research", "Analysis"];

  const filtered =
    selectedCategory === "All"
      ? articles
      : articles.filter((a) => a.category === selectedCategory);

  return (
    <div>
      <NewPageHeader
        title={
          <>
            Healthcare
            <br />
            in America
          </>
        }
        highlightedText="Transparency Matters"
        subtitle="Articles, research, and analysis on U.S. drug pricing, healthcare policy, and what you can do to make a difference."
        backgroundImage="/images/newpage.jpg"
        highlightColor="#43C5B9"
        showFlagImage={false}
      />

      <Container maxWidth="lg" style={{ padding: "60px 24px 100px" }}>

        {/* Category Filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "48px" }}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              style={{
                padding: "8px 22px",
                borderRadius: "999px",
                border: selectedCategory === cat ? "none" : "1.5px solid rgba(67,197,185,0.35)",
                background: selectedCategory === cat
                  ? "linear-gradient(135deg, #43C5B9, #2db5a8)"
                  : "transparent",
                color: selectedCategory === cat ? "#fff" : "#062a3f",
                fontWeight: "600",
                fontSize: "14px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                fontFamily: "'Roboto', sans-serif",
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
            gap: "28px",
          }}
        >
          {filtered.map((article) => (
            <div
              key={article.id}
              style={{
                background: "rgba(255,255,255,0.7)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                borderRadius: "18px",
                overflow: "hidden",
                border: "1px solid rgba(67,197,185,0.12)",
                boxShadow: "0 4px 24px rgba(6,42,63,0.06)",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
                display: "flex",
                flexDirection: "column",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 12px 40px rgba(6,42,63,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 24px rgba(6,42,63,0.06)";
              }}
            >
              {/* Card top accent bar */}
              <div style={{ height: "4px", background: `linear-gradient(90deg, ${article.color}, transparent)` }} />

              <div style={{ padding: "28px", display: "flex", flexDirection: "column", flex: 1 }}>
                {/* Meta row */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "700",
                      color: article.color,
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                    }}
                  >
                    {article.category}
                  </span>
                  <span style={{ fontSize: "12px", color: "#82929b" }}>·</span>
                  <span style={{ fontSize: "12px", color: "#82929b" }}>{article.date}</span>
                  <span style={{ fontSize: "12px", color: "#82929b" }}>·</span>
                  <span style={{ fontSize: "12px", color: "#82929b" }}>{article.readTime}</span>
                </div>

                {/* Title */}
                <Typography
                  variant="h6"
                  style={{
                    fontWeight: "700",
                    color: "#062a3f",
                    marginBottom: "12px",
                    lineHeight: "1.35",
                    fontSize: "18px",
                    fontFamily: "'Roboto', sans-serif",
                  }}
                >
                  {article.title}
                </Typography>

                {/* Excerpt */}
                <Typography
                  variant="body2"
                  style={{
                    color: "#4a6070",
                    lineHeight: "1.65",
                    marginBottom: "20px",
                    fontSize: "14px",
                    flex: 1,
                  }}
                >
                  {article.excerpt}
                </Typography>

                {/* Source */}
                <Typography
                  style={{
                    fontSize: "12px",
                    color: "#82929b",
                    marginBottom: "16px",
                    fontStyle: "italic",
                  }}
                >
                  Source: {article.source}
                </Typography>

                {/* Tag + Read more */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span
                    style={{
                      background: `${article.color}18`,
                      color: article.color,
                      borderRadius: "999px",
                      padding: "4px 12px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    #{article.tag}
                  </span>
                  <a
                    href={article.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: article.color,
                      fontWeight: "600",
                      fontSize: "13px",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      fontFamily: "'Roboto', sans-serif",
                    }}
                  >
                    Read More <span style={{ fontSize: "16px" }}>→</span>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Subscribe CTA */}
        <div
          style={{
            marginTop: "72px",
            background: "linear-gradient(135deg, rgba(67,197,185,0.1), rgba(45,181,168,0.06))",
            borderRadius: "20px",
            padding: "48px",
            textAlign: "center",
            border: "1px solid rgba(67,197,185,0.15)",
          }}
        >
          <Typography
            variant="h4"
            style={{
              fontWeight: "800",
              color: "#062a3f",
              marginBottom: "12px",
              fontFamily: "'Roboto', sans-serif",
            }}
          >
            Stay Informed
          </Typography>
          <Typography
            style={{
              color: "#4a6070",
              maxWidth: "500px",
              margin: "0 auto 28px",
              lineHeight: "1.6",
            }}
          >
            Get the latest research and articles on U.S. healthcare pricing delivered straight to your inbox.
          </Typography>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <input
              type="email"
              placeholder="Enter your email"
              style={{
                padding: "12px 20px",
                borderRadius: "999px",
                border: "1.5px solid rgba(67,197,185,0.3)",
                fontSize: "15px",
                outline: "none",
                width: "280px",
                fontFamily: "'Roboto', sans-serif",
              }}
            />
            <button
              style={{
                padding: "12px 28px",
                borderRadius: "999px",
                background: "linear-gradient(135deg, #43C5B9, #2db5a8)",
                color: "#fff",
                fontWeight: "700",
                fontSize: "15px",
                border: "none",
                cursor: "pointer",
                fontFamily: "'Roboto', sans-serif",
                boxShadow: "0 4px 15px rgba(67,197,185,0.3)",
              }}
            >
              Subscribe
            </button>
          </div>
        </div>
      </Container>


    </div>
  );
}
